import React, { useEffect, useState } from 'react';
import { adminAPI } from '../../services/api';
import { TableSkeleton, EmptyState } from '../../components/skeletons';

const S = {
  page: { padding: 24, background: 'var(--cream)', minHeight: '100vh' },
  title: { fontFamily: 'var(--font-serif)', fontSize: 28, fontWeight: 700, color: 'var(--char)', marginBottom: 4 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 24 },
  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: 16, padding: 24, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', marginBottom: 24 },
  settingsCard: { background: '#fff', border: '1px solid var(--border)', borderRadius: 16, padding: 20, boxShadow: '0 4px 12px rgba(0,0,0,0.03)', marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 },
  table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 14 },
  th: { padding: '12px 16px', borderBottom: '2px solid var(--border)', color: 'var(--muted)', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 },
  td: { padding: '16px', borderBottom: '1px solid var(--border)', color: 'var(--char)', verticalAlign: 'middle' },
  btn: { background: '#4C1D95', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' },
  emptyState: { padding: '48px 24px', textAlign: 'center', color: 'var(--muted)', background: '#fff', borderRadius: 16, border: '1px solid var(--border)' },
  input: { padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, outline: 'none', transition: 'border-color 0.2s', width: 140, boxSizing: 'border-box' },
  label: { fontSize: 11, fontWeight: 700, color: 'var(--char)', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', marginBottom: 6 },
  alert: { padding: '12px 16px', borderRadius: 8, marginBottom: 20, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, background: '#ECFDF5', color: '#065F46', border: '1px solid #A7F3D0' },
  badge: (active) => ({
    background: active ? '#ECFDF5' : '#FFFBEB',
    color: active ? '#065F46' : '#B45309',
    border: `1.5px solid ${active ? '#A7F3D0' : '#FCD34D'}`,
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700
  }),
};

export default function CreditRequests() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [autoCreditAmount, setAutoCreditAmount] = useState(300);
  const [isSandboxMode, setIsSandboxMode] = useState(false);
  const [savingSetting, setSavingSetting] = useState(false);
  const [toast, setToast] = useState(null);

  const loadData = async () => {
    setLoading(true);
    try {
      const [vRes, sRes] = await Promise.all([
        adminAPI.getCreditRequests(),
        adminAPI.getSettings()
      ]);

      setVendors(vRes.data.data || []);

      const settings = sRes.data.data || [];
      const modeSetting = settings.find(item => item.key === 'system_mode');
      const autoAmtSetting = settings.find(item => item.key === 'auto_credit_amount');

      if (modeSetting) setIsSandboxMode(modeSetting.value === 'local_production');
      if (autoAmtSetting) setAutoCreditAmount(Number(autoAmtSetting.value));
    } catch (err) {
      console.error('Failed to load credit request data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveSetting = async (e) => {
    e.preventDefault();
    const amt = Number(autoCreditAmount);
    if (isNaN(amt) || amt < 0) {
      alert('Please enter a valid positive auto credit amount.');
      return;
    }

    setSavingSetting(true);
    try {
      await adminAPI.updateSettings({ auto_credit_amount: amt });
      setToast(`Auto Credit Amount updated to ₹${amt} successfully!`);
      setTimeout(() => setToast(null), 4000);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update auto credit setting.');
    } finally {
      setSavingSetting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    const timePart = date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
    return `${datePart}, ${timePart}`;
  };

  return (
    <div style={S.page}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={S.title}>Credit Request Dashboard</div>
          <div style={S.subtitle}>Configure auto-credit wallet amounts and view logs of vendors requesting sandbox credits.</div>
        </div>
        <div>
          <span style={S.badge(isSandboxMode)}>
            {isSandboxMode ? '🟢 Sandbox Mode Active (Local Prod)' : '🟡 Live Production Mode'}
          </span>
        </div>
      </div>

      {toast && (
        <div style={S.alert}>
          <span>✅</span>
          <span>{toast}</span>
        </div>
      )}

      {/* Settings Card */}
      <div style={S.settingsCard}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <div style={{ fontSize: 32 }}>⚙️</div>
          <div>
            <div style={{ fontWeight: 700, color: 'var(--char)' }}>Auto Credit Configuration</div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>
              Set the wallet balance amount instantly credited to vendors requesting credits.
            </div>
          </div>
        </div>
        <form onSubmit={handleSaveSetting} style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
          <div>
            <label style={S.label}>Auto Credit Amount (₹)</label>
            <input
              style={S.input}
              type="number"
              min="0"
              required
              value={autoCreditAmount}
              onChange={e => setAutoCreditAmount(e.target.value)}
              onWheel={e => e.target.blur()}
              placeholder="e.g. 300"
            />
          </div>
          <button type="submit" style={S.btn} disabled={savingSetting}>
            {savingSetting ? 'Saving...' : 'Save Settings'}
          </button>
        </form>
      </div>

      {loading ? (
        <TableSkeleton rows={5} columns={5} />
      ) : vendors.length === 0 ? (
        <EmptyState
          title="No Credit Request History"
          description="No vendors have requested sandbox credits yet."
        />
      ) : (
        <div style={S.card}>
          <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--char)', marginBottom: 16 }}>Vendors Requesting Credit</div>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Vendor / Firm</th>
                <th style={S.th}>Contact Info</th>
                <th style={S.th}>Current Balance</th>
                <th style={S.th}>Total Request Count</th>
                <th style={S.th}>Latest Request Date</th>
              </tr>
            </thead>
            <tbody>
              {vendors.map(v => (
                <tr key={v.vendorId}>
                  <td style={S.td}>
                    <div style={{ fontWeight: 700, fontSize: 15 }}>{v.firmName || 'N/A'}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{v.vendorName || 'N/A'}</div>
                  </td>
                  <td style={S.td}>
                    <div style={{ fontSize: 13 }}>{v.vendorEmail || 'N/A'}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>{v.vendorMobile || 'N/A'}</div>
                  </td>
                  <td style={S.td}>
                    <span style={{ fontWeight: 700 }}>
                      ₹{(v.walletBalance ?? 0).toLocaleString('en-IN')}
                    </span>
                  </td>
                  <td style={S.td}>
                    <span style={{ fontWeight: 600, color: 'var(--char)' }}>
                      {v.totalRequests} request(s)
                    </span>
                  </td>
                  <td style={S.td}>
                    <div style={{ fontSize: 13 }}>{formatDate(v.latestRequestDate)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
